'use client'

import { Paper, SxProps, Theme } from '@mui/material'
import { styled } from '@mui/system'
import { ReactElement } from 'react'
import { tss } from 'tss-react/mui'
import AvatarStack from '../Avatar/AvatarStack'
import { TIocCode } from '../Flag/types'
import Metadata from '../Metadata/Metadata'
import FlagStack from './FlagStack'
import { CategoryPill, CommunityPill } from './Pills'
import TitleLink from './TitleLink'

// Allowing this import because it's just a type and won't affect tree-shaking the JS bundles

import { IconType } from '../Icon'

export type CardImageAspectRatio = '3/2' | '5/2'

interface CardBase {
    img: string
    imgAspectRatio?: CardImageAspectRatio
    title: string
    TitleIcon?: IconType
    meta1?: { Icon?: IconType; text: string }
    meta2?: { Icon?: IconType; text: string }
    linkWrapper: (children: ReactElement) => ReactElement
    tertiaryAction?: ReactElement
    sx?: SxProps<Theme>
    className?: string
}

interface CardWithAvatars extends CardBase {
    avatars?: string[]
    avatarsMoreOverride?: string
    category?: never
    community?: never
    flag?: never
}

interface CardWithCategory extends CardBase {
    avatars?: never
    avatarsMoreOverride?: never
    category?: string
    community?: string
    flag?: never
}

interface CardWithoutAdornment extends CardBase {
    avatars?: never
    avatarsMoreOverride?: never
    category?: never
    community?: never
    flag?: never
}

interface CardWithFlag extends CardBase {
    avatars?: never
    avatarsMoreOverride?: never
    category?: never
    community?: never
    flag?: TIocCode
}

export type CardProps = CardWithoutAdornment | CardWithAvatars | CardWithCategory | CardWithFlag

const useStyles = tss.create(({ theme }) => ({
    wrapper: {
        minHeight: '150px',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        flexGrow: 1,
        flexBasis: 0,
        overflow: 'hidden',
        border: 'none',

        '&:hover': {
            boxShadow: 'none',
        },

        [theme.breakpoints.up('sm')]: {
            '&:hover': {
                boxShadow: theme.shadows[5],
            },
        },
    },
    fittedImageWrapper: {
        display: 'block',
        overflow: 'hidden',
        borderRadius: theme.shape.borderRadius * 2,
    },
    tertiaryWrapper: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
    },
    metadataWrapper: {
        padding: theme.spacing(2),
        paddingTop: theme.spacing(1),
    },
    metadataWrapperWithExtras: {
        paddingTop: theme.spacing(0.5),
    },
    metadata: {
        paddingTop: 0,
        marginTop: theme.spacing(0.5),
    },
    avatarStack: {
        marginTop: theme.spacing(-3.5),
        marginLeft: theme.spacing(1.5),
    },
    positionRelative: {
        position: 'relative',
    },
}))

const FittedImage = styled('img', {
    shouldForwardProp: (prop) => prop !== 'aspectRatio',
})<{ aspectRatio: CardImageAspectRatio }>(({ aspectRatio }) => ({
    width: '100%',
    display: 'block',
    aspectRatio: aspectRatio,
    objectFit: 'cover',
}))

function Card({
    title,
    TitleIcon,
    img,
    imgAspectRatio = '5/2',
    meta1,
    meta2,
    avatars,
    avatarsMoreOverride,
    linkWrapper,
    tertiaryAction,
    sx,
    category,
    community,
    flag,
    className,
}: CardProps): ReactElement {
    const { classes, cx } = useStyles()
    const hasAvatars = avatars && avatars.length > 0
    const hasExtras = Boolean(hasAvatars || community || category)

    return (
        <Paper sx={sx} className={cx(classes.wrapper, className)}>
            <div className={classes.positionRelative}>
                {linkWrapper(
                    <FittedImage
                        src={img}
                        alt={title}
                        aspectRatio={imgAspectRatio}
                        className={classes.fittedImageWrapper}
                        data-testid="card-image-link"
                    />
                )}
                {tertiaryAction ? <div className={classes.tertiaryWrapper}>{tertiaryAction}</div> : null}
            </div>
            <CategoryPill category={category} showCommunity={Boolean(community)} />
            <CommunityPill community={community} />
            {hasAvatars
                ? linkWrapper(
                      <AvatarStack
                          avatars={avatars}
                          moreOverride={avatarsMoreOverride}
                          className={classes.avatarStack}
                      />
                  )
                : null}
            {flag ? linkWrapper(<FlagStack flag={flag} />) : null}
            <div className={cx(classes.metadataWrapper, hasExtras && classes.metadataWrapperWithExtras)}>
                {linkWrapper(<TitleLink text={title} Icon={TitleIcon} />)}
                {meta1 ? <Metadata {...meta1} clamp={1} className={classes.metadata} /> : null}
                {meta2 ? <Metadata {...meta2} clamp={1} className={classes.metadata} /> : null}
            </div>
        </Paper>
    )
}

export default Card
